export const POST_LOGIN_REDIRECT_KEY = "mp_post_login_redirect"

// Only same-origin relative paths are safe to redirect to - reject anything
// that could send the browser to an external site (e.g. "https://evil.com"
// or the protocol-relative "//evil.com"), since `redir` comes straight from
// a URL an attacker could craft.
function isSafeRelativePath(path: string): boolean {
  return (
    path.startsWith("/") && !path.startsWith("//") && !path.includes("://")
  )
}

// Captures a `redir` query param off the current URL (if present) into
// sessionStorage, then strips it from the URL via history.replace so it
// doesn't linger in the address bar / get bookmarked. Call this once, early,
// before auth completes - it works regardless of which unauth route (manual
// login, OAuth, SAML) the user lands on first.
export function capturePostLoginRedirect(history: {
  replace: (location: { pathname: string; search: string }) => void
}): void {
  const params = new URLSearchParams(window.location.search)
  const redir = params.get("redir")
  if (!redir || !isSafeRelativePath(redir)) {
    return
  }

  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, redir)

  params.delete("redir")
  const strippedSearch = params.toString()
  history.replace({
    pathname: window.location.pathname,
    search: strippedSearch ? `?${strippedSearch}` : ""
  })
}

// Reads back (and clears) the stored post-login redirect, if any. Clearing
// on read means it only ever fires once per stored value, not on every
// subsequent visit to the authed app.
export function consumePostLoginRedirect(): string | null {
  const redir = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
  if (!redir) {
    return null
  }

  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  return isSafeRelativePath(redir) ? redir : null
}
